import { Context } from "csi/def/Context";
import Number from "csi/heading/Number";
import { TableWidget } from "csi/table/Widget";
import QueryResult from "csi/view/_query/Result";
import declare from "dojo/_base/declare";

const RecipeLineItemNumber = declare(Number, {

	getAmountInFoodBOM(ctx: Context): number {
		const view = ctx.view as TableWidget;
		const nLevel = ctx.queryResult.hl + 1 - view.getMainLevel();

		// 1st level or if not RecipeLineItem
		if (nLevel == 1 || ctx.getNode().$Type !== "RecipeLineItem") {
			return ctx.getNode().QtyDefault;
		} else {
			// Level 2 and above
			// Calculate QtyDefault for RecipeLineItem based on the levels below it
			const totalAmount = ctx.queryResult.getContext()
				.getParent().QtyDefault;
			const pctOfTotal = this.getPercentOfTotalInFoodBOM(ctx);
			ctx.getNode().QtyDefault = totalAmount * pctOfTotal;
			return ctx.getNode().QtyDefault;
		}
	},

	getPercentOfTotalInFoodBOM(ctx: Context): number {
		return this._getPercentOfTotalFromParentLevelInFoodBOM(ctx, ctx.queryResult);
	},

	/** On multiple level Recipe in FoodBOM calculate the RecipeLineItem's "% of Total" */
	_getPercentOfTotalFromParentLevelInFoodBOM(ctx: Context, qr: QueryResult): number {
		let value = qr.getNode().PctOfLevel;

		// Set percent to 100% if top level foodBOMLineItem or the level above
		if (qr.getNode().$Type == "FoodBOMLineItem" || ctx.queryResult.hl > qr.hl) {
			value = 1;
		}

		const view = ctx.view as TableWidget;
		const nLevel = qr.hl + 1 - view.getMainLevel();
		if (nLevel > 1) {
			// On level-2 or above, the Percent of Total should be multiplication of PctOfLevel of current and all the parent levels
			value = value * this._getPercentOfTotalFromParentLevelInFoodBOM(ctx, qr.parentResult);
		}
		return value;
	},

	/** On multiple level Recipe calculate the display value of RecipeLineItem's QtyDefault / AmountAfterH2OLoss  (C8-36606, C8-37600) */
	getAmount(ctx: Context, afterH2OLoss: boolean): number {
		const view = ctx.view as TableWidget;
		// C8-57722: If ctx.queryResult is null, then renderer is being refreshed under Edit Comment context (comments are only allowed on 1st level of hierarchy)
		const hierarchyLevel = ctx.queryResult ? ctx.queryResult.hl : 0;
		const nLevel = hierarchyLevel + 1 - view.getMainLevel();

		// 1st level RecipeLineItem
		if (nLevel == 1) {
			return afterH2OLoss ? ctx.getNode().AmountAfterH2OLoss : ctx.getNode().QtyDefault;
		}

		const recipeRevision = view.getViewNode();
		// 2nd or deeper level RecipeLineItem
		if (nLevel > 1 && recipeRevision) {
			const totalAmount = afterH2OLoss ? recipeRevision.AmountAfterH2OLoss : recipeRevision.TotalAmount;
			const pctOfTotal = this.getPercentOfTotal(ctx, afterH2OLoss);
			return totalAmount * pctOfTotal;
		}

		return 0.0;
	},

	/** Calculate and return the RecipeLineItem's "% of Total" on multiple level Recipe (C8-36606) */
	getPercentOfTotal(ctx: Context, afterH2OLoss: boolean): number {
		return this._getPercentOfTotalFromParentLevel(ctx, ctx.queryResult, afterH2OLoss);
	},

	/** On multiple level Recipe calculate the RecipeLineItem's "% of Total"  (C8-36606) */
	_getPercentOfTotalFromParentLevel(ctx: Context, qr: QueryResult, afterH2OLoss: boolean): number {
		let value = afterH2OLoss ? qr.getNode().PctOfLevelAfterH2OLoss : qr.getNode().PctOfLevel;

		const view = ctx.view as TableWidget;
		const nLevel = qr.hl + 1 - view.getMainLevel();
		if (nLevel > 1) {
			// On level-2 or above, the Percent of Total should be multiplication of PctOfLevel of current and all the parent levels
			value = value * this._getPercentOfTotalFromParentLevel(ctx, qr.parentResult, afterH2OLoss);
		}

		return value;
	},
});

export = RecipeLineItemNumber;